import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBalance } from '../context/BalanceContext';
import axios from 'axios';
import { usePrices } from '../context/PriceContext';
import { formatCompactNumber, formatCurrency, formatChange } from '../utils/numberFormat';
import LoadingSkeleton from '../components/LoadingSkeleton';


const normalizeSymbol = (symbol = '') => symbol.toUpperCase();

export default function Dashboard() {
  const { currentUser, loading: loadingAuth, userData } = useAuth();
  const navigate = useNavigate();
  const { balanceData } = useBalance();
  const { prices, priceChanges } = usePrices();
  const balances = useMemo(() => {
    const accounts = balanceData?.accountBalances || {};
    const aggregated = {};

    Object.entries(accounts).forEach(([accountKey, coins = {}]) => {
      // Exclude arbitrageReserved — those are locked funds, not spendable
      if (accountKey === 'arbitrageReserved') return;
      Object.entries(coins).forEach(([coinKey, rawAmount]) => {
        const symbol = normalizeSymbol(coinKey);
        const numeric = Number(rawAmount);
        if (!Number.isFinite(numeric)) {
          return;
        }
        aggregated[symbol] = (aggregated[symbol] || 0) + numeric;
      });
    });

    return aggregated;
  }, [balanceData]);

  const [coins, setCoins] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('BINANCE:BTCUSDT');
  const [showChartModal, setShowChartModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());
  const [visibleColumns, setVisibleColumns] = useState(['balance', 'price']);
  const [rateLimitedUntil, setRateLimitedUntil] = useState(0);

  const columnDefinitions = useMemo(
    () => [
      {
        key: 'balance',
        label: 'Balance',
        align: 'text-right',
        render: (coin) => {
          const symbol = coin.symbol?.toUpperCase();
          const value = balances[symbol];
          return value !== undefined && value !== null
            ? formatCompactNumber(value)
            : '-';
        }
      },
      {
        key: 'price',
        label: 'Price',
        align: 'text-right',
        render: (coin) => formatCurrency(coin.current_price)
      },
      {
        key: 'change',
        label: '24h Change',
        align: 'text-right',
        render: (coin) => ({
          value: formatChange(coin.price_change_percentage_24h).value,
          isPositive: (coin.price_change_percentage_24h ?? 0) >= 0
        })
      },
      {
        key: 'marketCap',
        label: 'Market Cap',
        align: 'text-right',
        render: (coin) => formatCurrency(coin.market_cap)
      }
    ],
    [balances]
  );

  const handleToggleColumn = useCallback(
    (columnKey) => {
      setVisibleColumns((current) => {
        const isActive = current.includes(columnKey);
        if (isActive && current.length === 1) {
          return current; // always keep at least one column visible
        }
        if (isActive) {
          return current.filter((key) => key !== columnKey);
        }
        return [...current, columnKey];
      });
    },
    []
  );

  // Helper to map coin symbols to TradingView symbols (default to BINANCE:SYMBOLUSDT)
  const getTradingViewSymbol = (coin) => {
    if (coin.symbol.toUpperCase() === 'USDT') return 'BINANCE:USDTUSD';
    if (coin.symbol.toUpperCase() === 'USD') return 'BINANCE:BTCUSDT';
    return `BINANCE:${coin.symbol.toUpperCase()}USDT`;
  };

  // Inject TradingView widget for the selected symbol (only when modal is open)
  useEffect(() => {
    if (!showChartModal) return;
    const prevScript = document.getElementById('tradingview-widget-script');
    if (prevScript) prevScript.remove();
    const prevChart = document.getElementById('tradingview_chart');
    if (prevChart) prevChart.innerHTML = '';

    const script = document.createElement('script');
    script.id = 'tradingview-widget-script';
    script.type = 'text/javascript';
    script.src = 'https://s3.tradingview.com/tv.js';
    script.onload = () => {
      if (window.TradingView) {
        new window.TradingView.widget({
          width: '100%',
          height: 500,
          symbol: selectedSymbol,
          interval: '60',
          timezone: 'Etc/UTC',
          theme: 'dark',
          style: '1',
          locale: 'en',
          toolbar_bg: '#1e293b',
          enable_publishing: false,
          allow_symbol_change: true,
          container_id: 'tradingview_chart',
        });
      }
    };
    document.body.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [selectedSymbol, showChartModal]);

  const fetchCoins = useCallback(async (force = false) => {
    const now = Date.now();
    // If we've recently hit a 429 from CoinGecko, back off until the cooldown expires
    if (now < rateLimitedUntil) {
      return;
    }
    
    // Only check rate limit if not forcing a refresh
    if (!force && (isRefreshing || (now - lastRefreshTime < 10000))) {
      return;
    }
    
    setIsRefreshing(true);
    try {
      const response = await axios.get(
        'https://api.coingecko.com/api/v3/coins/markets',
        {
          params: {
            vs_currency: 'usd',
            order: 'market_cap_desc',
            per_page: 100,
            page: 1,
            sparkline: false,
            price_change_percentage: '24h'
          },
          headers: {
            'Accept': 'application/json'
          }
        }
      );
      
      if (response.data && Array.isArray(response.data)) {
        const apiCoins = response.data.map(coin => ({
          ...coin,
          symbol: coin.symbol.toUpperCase()
        }));
        
        // Add any balance coins that aren't in the API response
        const balanceSymbols = Object.keys(balances || {});
        const extraCoins = balanceSymbols.filter(sym => 
          !apiCoins.some(c => c.symbol === sym)
        );
        
        const extraCoinObjects = extraCoins.map(sym => ({
          id: sym,
          symbol: sym,
          name: sym,
          image: '',
          current_price: prices?.[sym] || 0,
          price_change_percentage_24h: priceChanges?.[sym] || 0,
          market_cap: 0,
          price_change_24h: 0,
          total_volume: 0,
          high_24h: 0,
          low_24h: 0,
          last_updated: new Date().toISOString()
        }));
        
        setCoins([...apiCoins, ...extraCoinObjects]);
      } else {
        console.error('Invalid response data format:', response.data);
        // Don't clear coins on error to keep showing the last good data
      }
      setLastRefreshTime(now);
    } catch (error) {
      if (error?.response?.status === 429) {
        // Too many requests – set a cooldown to avoid spamming CoinGecko
        console.warn('CoinGecko rate limit hit; backing off for 15 minutes.');
        setRateLimitedUntil(now + 15 * 60 * 1000);
      } else if (error?.code === 'ERR_NETWORK' || !error?.response) {
        // Network/CORS issue – also back off for a while
        console.warn('Network/CORS issue when fetching coins; backing off for 10 minutes.');
        setRateLimitedUntil(now + 10 * 60 * 1000);
      } else {
        console.error("Error fetching coins:", error);
      }
      // Don't clear coins on error to keep showing the last good data
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, lastRefreshTime, balances, prices, priceChanges, rateLimitedUntil]);

  // Initial load and setup
  useEffect(() => {
    const handleScroll = () => {
      // Only trigger refresh if scrolled to top and not already refreshing
      if (window.scrollY < 100 && !isRefreshing) {
        fetchCoins(true);
      }
    };

    // Initial data load
    if (coins.length === 0) {
      fetchCoins(true);
    }
    
    // Set up scroll listener for pull-to-refresh
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Set up auto-refresh every 5 minutes
    const refreshInterval = setInterval(() => {
      fetchCoins(true);
    }, 5 * 60 * 1000);
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(refreshInterval);
    };
  }, [isRefreshing, coins.length, fetchCoins]);

  if (loadingAuth || !currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSkeleton width="80%" height={48} />
        <span className="ml-4 text-lg text-gray-300">Loading balances...</span>
      </div>
    );
  }

  // balanceError currently unused with real-time snapshots




  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-navy via-navyLight to-slate-950 text-slate-50">

      <div className="flex-1 flex flex-col p-4 md:p-8">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <h1 className="text-4xl font-bold">Dashboard</h1>
          {isRefreshing ? (
            <div className="text-sm text-blue-400">Updating...</div>
          ) : (
            <button
              type="button"
              onClick={() => fetchCoins(true)}
              className="rounded-full border border-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-200 transition hover:border-white/40 hover:bg-white/10"
            >
              Refresh Market Data
            </button>
          )}
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide text-gray-300/80">
          <span className="mr-1 text-gray-400">Display Columns:</span>
          {columnDefinitions.map((column) => {
            const active = visibleColumns.includes(column.key);
            return (
              <button
                key={`toggle-${column.key}`}
                type="button"
                onClick={() => handleToggleColumn(column.key)}
                className={`rounded-full border px-3 py-1 font-semibold transition ${
                  active
                    ? 'border-blue-400/60 bg-blue-500/15 text-blue-100'
                    : 'border-white/15 bg-white/5 text-gray-300 hover:border-white/30 hover:bg-white/10'
                }`}
              >
                {column.label}
              </button>
            );
          })}
        </div>

        {/* Verification CTA */}
        {userData && userData.verification_status !== 'verified' && (
          <div className={`mb-6 flex items-center justify-between gap-4 rounded-2xl border p-4 ${
            userData.verification_status === 'pending'
              ? 'border-amber-500/30 bg-amber-500/8'
              : userData.verification_status === 'rejected'
              ? 'border-red-500/30 bg-red-500/8'
              : 'border-indigo-500/30 bg-indigo-500/8'
          }`}>
            <div>
              <p className={`text-sm font-bold ${
                userData.verification_status === 'pending' ? 'text-amber-300'
                : userData.verification_status === 'rejected' ? 'text-red-300'
                : 'text-white'
              }`}>
                {userData.verification_status === 'pending'
                  ? 'Verification in review'
                  : userData.verification_status === 'rejected'
                  ? 'Verification not approved'
                  : 'Verify your account'}
              </p>
              <p className="mt-0.5 text-xs text-gray-400">
                {userData.verification_status === 'pending'
                  ? 'Our team is reviewing your documents. No action needed.'
                  : 'Complete verification to unlock full account access.'}
              </p>
            </div>
            {userData.verification_status !== 'pending' && (
              <button
                onClick={() => navigate('/profile')}
                className="shrink-0 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition hover:bg-indigo-500"
              >
                Verify Now
              </button>
            )}
          </div>
        )}

        {/* Locked Funds Notice */}
        {(() => {
          const reserved = balanceData?.accountBalances?.arbitrageReserved || {};
          const totalLocked = Object.values(reserved).reduce((sum, v) => sum + Number(v || 0), 0);
          if (totalLocked <= 0) return null;
          return (
            <div className="mb-6 flex items-center gap-3 rounded-2xl border border-indigo-500/20 bg-indigo-500/6 px-4 py-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-500/15 text-indigo-300 text-sm">🔒</span>
              <div>
                <p className="text-sm font-semibold text-indigo-200">Locked Arbitrage Funds</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  You have funds currently locked in active arbitrage positions. These are not included in the balances shown below.
                  <span
                    className="ml-1 cursor-pointer text-indigo-400 underline underline-offset-2 hover:text-indigo-300"
                    onClick={() => window.location.href = '/arbitrage'}
                  >View positions →</span>
                </p>
              </div>
            </div>
          );
        })()}

        {/* Coin Table Section (existing) */}
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-700 text-sm">
            <thead>
              <tr className="bg-gray-700 text-white">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Coin</th>
                {columnDefinitions
                  .filter((column) => visibleColumns.includes(column.key))
                  .map((column) => (
                    <th
                      key={`head-${column.key}`}
                      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide ${column.align}`}
                    >
                      {column.label}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {coins.map((coin, index) => (
                <tr
                  key={coin.id}
                  className="border-t border-gray-700 hover:bg-gray-800 cursor-pointer"
                  onClick={() => {
                    setSelectedSymbol(getTradingViewSymbol(coin));
                    setShowChartModal(true);
                  }}
                  tabIndex={0}
                  onKeyPress={e => {
                    if (e.key === 'Enter') {
                      setSelectedSymbol(getTradingViewSymbol(coin));
                      setShowChartModal(true);
                    }
                  }}
                  style={{ outline: 'none' }}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={coin.image} alt={coin.name} className="h-6 w-6 rounded-full sm:h-8 sm:w-8" />
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-white">{coin.name}</span>
                        <span className="text-xs uppercase tracking-wide text-gray-400">{coin.symbol}</span>
                      </div>
                    </div>
                  </td>
                  {columnDefinitions
                    .filter((column) => visibleColumns.includes(column.key))
                    .map((column) => {
                      const cell = column.render(coin);
                      if (column.key === 'change') {
                        return (
                          <td
                            key={`cell-${coin.id}-${column.key}`}
                            className={`px-4 py-3 text-sm font-semibold ${
                              cell.isPositive ? 'text-green-400' : 'text-red-400'
                            } ${column.align}`}
                          >
                            {cell.value}
                          </td>
                        );
                      }

                      return (
                        <td
                          key={`cell-${coin.id}-${column.key}`}
                          className={`px-4 py-3 text-sm ${column.align}`}
                        >
                          {cell}
                        </td>
                      );
                    })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>


        {/* Chart Modal (existing) */}
        {showChartModal && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50"
            onClick={() => setShowChartModal(false)}
            tabIndex={-1}
          >
            <div
              className="bg-background text-white p-6 rounded-xl shadow-2xl border border-gray-700 relative w-full max-w-3xl"
              style={{ minHeight: 540 }}
              onClick={e => e.stopPropagation()}
            >
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-white text-2xl font-bold focus:outline-none"
                onClick={() => setShowChartModal(false)}
                aria-label="Close chart"
              >
                ×
              </button>
              <h3 className="text-xl font-semibold mb-2">Live Chart</h3>
              <div className="tradingview-widget-container" style={{ height: 500 }}>
                <div id="tradingview_chart" style={{ height: 500 }} />
              </div>
              <div className="mt-2 text-gray-300 text-sm">
                Showing: <span className="font-bold text-white">{selectedSymbol.replace('BINANCE:', '').replace('USDT', '/USDT')}</span> (Binance)
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
